import { Component, inject, OnInit } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { catchError } from 'rxjs/operators';
import { of } from 'rxjs';
import { CompaniesService } from '../../../services/companies.service';
import {
  formatDatePickerValue,
  parseDatePickerValue
} from '../../../utils/datepicker-value-parser';
import { ClientNote } from '../../../models/client';
import { ModalService } from '../../../services/modal.service';
import { ModalStates } from '../../../models/modal';
import { ApiResponse } from '../../../models/api-response';
import { ToastService } from '../../../services/toast.service';
import _ from 'underscore';

@Component({
  selector: 'app-client-note',
  templateUrl: './client-note.component.html',
  styleUrls: ['./client-note.component.css'],
  standalone: false
})
export class ClientNoteComponent implements OnInit {
  clientNoteHistory: ClientNote[] = [];
  clientNote: ClientNote;
  clientId: string;
  expireOnDate: any;
  note: string;

  private readonly toastService = inject(ToastService);

  constructor(
    private companyService: CompaniesService,
    private activatedRoute: ActivatedRoute,
    private modalService: ModalService,
    private ngModalService: NgbModal
  ) {
    this.saveNote = _.debounce(this.saveNote, 1000);
  }

  ngOnInit(): void {
    this.activatedRoute.params.subscribe(params => {
      this.clientId = params['client_code'];
    });

    this.getActiveClientNote();
  }

  // #region public methods

  public archiveNote() {
    this.companyService.archiveClientNote(this.clientId, this.clientNote).subscribe(() => {
      this.clientNote = null;
      this.expireOnDate = null;
      this.note = null;
    });
  }

  public getActiveClientNote() {
    // get the active client note, if present
    this.companyService.getActiveClientNote(this.clientId).subscribe(resp => {
      // need to create a bootstrap datepicker object if expires on exists
      if (resp?.ExpiresOn) {
        this.expireOnDate = formatDatePickerValue(new Date(resp.ExpiresOn));
      }

      this.note = resp?.Note;
      this.clientNote = resp;
    });
  }

  public getClientNoteHistory(content) {
    this.companyService.getAllClientNotes(this.clientId).subscribe(resp => {
      this.clientNoteHistory = resp;
    });

    this.ngModalService.open(content, {
      size: 'lg',
      backdrop: 'static',
      keyboard: false
    });
  }

  public saveNote() {
    if (!this.clientNote) {
      this.clientNote = new ClientNote();
      this._setModelProps();

      // create note
      this.companyService.createClientNote(this.clientId, this.clientNote).subscribe(resp => {
        this.clientNote = resp;
        this.toastService.show('Save successful');
      });
    } else {
      // update existing note
      this._setModelProps();
      this.companyService
        .updateClientNote(this.clientId, this.clientNote)
        .pipe(
          catchError(err => {
            // LastModifiedOn needs to be passed back to the client
            // in order to render the proper timezone
            if (err && err.Message) {
              let msg = err.Message;
              if (err.Dates && err.Dates['LastModifiedOn']) {
                msg += ` on ${new Date(err.Dates['LastModifiedOn']).toLocaleString()}`;
              }

              msg +=
                '. Please copy your changes to your clipboard and refresh the note to continue.';
              this.modalService.showAlertModal(msg, ModalStates.Error);
            }

            return of(null);
          })
        )
        .subscribe((resp: ApiResponse<ClientNote>) => {
          if (!resp) {
            return;
          }

          this.clientNote = resp.Model;
          this.toastService.show('Save successful');
        });
    }
  }

  // #endregion

  // #region private methods

  private _setModelProps() {
    this.clientNote.Note = this.note || '';

    this.expireOnDate
      ? (this.clientNote.ExpiresOn = parseDatePickerValue(this.expireOnDate))
      : (this.clientNote.ExpiresOn = null);
  }

  // #endregion
}
